import { css } from 'aphrodite';
import moment from 'moment';
import { type ReactNode, useState } from 'react';
import { ChevronDown } from '../../../../../components/svgs/icons/ChevronDown';
import { ChevronRight } from '../../../../../components/svgs/icons/ChevronRight';
import { FileTextIcon } from '../../../../../components/svgs/icons/FileTextIcon';
import { UploadIconSimple } from '../../../../../components/svgs/icons/UploadIconSimple';
import { Button } from '../../../../../components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../../../components-v2/Collapsible';
import { Pill } from '../../../../../components-v2/Pill';
import { FileRequestState, type IFileRequestGroup } from './fileRequests';
import { styleSheet } from './styles';

function formatLongDate(dateStr: string) {
	return moment(dateStr).format('MMM D, YYYY');
}

type IProps = {
	group: IFileRequestGroup;
	senderName?: string;
	children?: ReactNode;
};

export const FileRequestCard = ({ group, senderName, children }: IProps) => {
	const { request, state, isNew, files } = group;
	const [areFilesOpen, setAreFilesOpen] = useState(false);
	const hasFiles = files.length > 0;
	const isWaiting = state === FileRequestState.Waiting;
	const meta = [
		request.requestedDate && `Requested ${formatLongDate(request.requestedDate)}`,
		request.expirationDate && `Expires ${formatLongDate(request.expirationDate)}`,
		senderName && `Sent by ${senderName}`,
	]
		.filter(Boolean)
		.join(' · ');

	return (
		<Collapsible
			open={areFilesOpen}
			onOpenChange={setAreFilesOpen}
			render={<section />}
			className={css(styleSheet.requestCard)}
			aria-label={request.title}
		>
			<div className={css(styleSheet.requestCardHeader)}>
				<div className={css(styleSheet.requestCardIcon)}>
					{hasFiles ? <FileTextIcon width={20} height={20} /> : <UploadIconSimple width={20} height={20} />}
				</div>
				<div className={css(styleSheet.requestCardSummary, !hasFiles && styleSheet.requestCardSummaryInline)}>
					<div className={css(styleSheet.requestCardTitleRow)}>
						<h3 className={css(styleSheet.requestCardTitle)}>{request.title}</h3>
						{isNew && <Pill variant='label' color='blue' label='New' size='small' />}
					</div>
					<div className={css(styleSheet.requestCardMeta)}>
						{meta}
						{isWaiting && !hasFiles && (
							<span className={css(styleSheet.requestCardMuted)}>{meta ? ' · ' : ''}No files yet</span>
						)}
					</div>
				</div>
				{isWaiting ? (
					<Pill variant='label' color='yellow' label='Waiting on upload' size='small' />
				) : (
					<Pill variant='label' color='green' label='File uploaded' size='small' />
				)}
			</div>
			{hasFiles && (
				<>
					<CollapsibleTrigger render={<Button variant='ghost' styles={[styleSheet.requestCardFilesTrigger]} />}>
						{areFilesOpen ? <ChevronDown /> : <ChevronRight />}
						{files.length} uploaded file{files.length !== 1 ? 's' : ''}
					</CollapsibleTrigger>
					<CollapsiblePanel>
						<div className={css(styleSheet.requestCardFiles)}>{children}</div>
					</CollapsiblePanel>
				</>
			)}
		</Collapsible>
	);
};
